import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useCheckUnique } from "../../Controllers/AuthController";
import EmailInput from "../../Components/Inputs/EmailInput";
import PasswordInput from "../../Components/Inputs/PasswordInput";
import TextInput from "../../Components/Inputs/TextInput";
import ContainedButton from "../../Components/Buttons/ContainedButton";
import { save, retrieve } from "../../helpers/StorageHelper";

export default function SignUp4() {
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPasswordMismatch, setShowPasswordMismatch] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const image = location.state?.image;

  const { control, formState, handleSubmit, watch } = useForm({
    mode: "onChange",
    defaultValues: {
      mobile: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const { isValid, errors } = formState;
  const { mutateAsync: checkUnique, isPending: isCheckingUnique } =
    useCheckUnique();

  const watchedPassword = watch("password");
  const watchedConfirmPassword = watch("confirmPassword");

  // Load stored values on component mount
  useEffect(() => {
    const storedEmail = retrieve("email");
    const storedMobile = retrieve("mobile");
    const storedPassword = retrieve("password");
    const storedConfirmedPassword = retrieve("confirmed_password");

    if (
      storedEmail ||
      storedMobile ||
      storedPassword ||
      storedConfirmedPassword
    ) {
      // Set form values if they exist in localStorage
      // This would require using setValue from react-hook-form
    }
  }, []);

  // Update password strength when password changes
  useEffect(() => {
    if (watchedPassword) {
      setPasswordStrength(checkPasswordStrength(watchedPassword));
    }
  }, [watchedPassword]);

  // Check password match when confirm password changes
  useEffect(() => {
    if (watchedConfirmPassword && watchedPassword) {
      setShowPasswordMismatch(watchedPassword !== watchedConfirmPassword);
    } else {
      setShowPasswordMismatch(false);
    }
  }, [watchedPassword, watchedConfirmPassword]);

  const checkPasswordStrength = (password) => {
    let criteria = 0;

    if (password.length >= 8) criteria++;
    if (/\d/.test(password)) criteria++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) criteria++;

    const strength = (criteria / 3) * 100;
    return strength;
  };

  // Form validation rules
  const validationRules = {
    mobile: {
      required: "Mobile number is required",
      pattern: {
        value: /^\d{10}$/,
        message: "Please enter a valid 10-digit phone number",
      },
    },
    email: {
      required: "Email is required",
      pattern: {
        value: /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: "Please enter a valid email address",
      },
    },
    password: {
      required: "Password is required",
      minLength: {
        value: 8,
        message: "Password must be at least 8 characters",
      },
      validate: (value) => {
        const hasNumber = /\d/.test(value);
        const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(value);
        if (!hasNumber) return "Password must contain at least one number";
        if (!hasSymbol) return "Password must contain at least one symbol";
        return true;
      },
    },
    confirmPassword: {
      required: "Please confirm your password",
      validate: (value) => {
        if (value !== watchedPassword) return "Passwords do not match";
        return true;
      },
    },
  };

  const onSubmit = async (data) => {
    try {
      // Check uniqueness using the controller
      const uniqueResult = await checkUnique({
        email: data.email,
        phone_number: data.mobile,
      });

      if (!uniqueResult.data.data.email_unique) {
        toast.error("This email is already registered");
        return;
      }

      if (!uniqueResult.data.data.phone_unique) {
        toast.error("This phone number is already registered");
        return;
      }

      // Save to localStorage and proceed if unique
      save("email", data.email);
      save("mobile", data.mobile);
      save("password", data.password);
      save("confirmed_password", data.confirmPassword);

      navigate("/signup/occupation", { state: { image } });
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(
        error?.response?.data?.message ||
          "An error occurred while checking uniqueness",
      );
    }
  };

  let passwordStrengthColor = "";

  if (passwordStrength < 34) {
    passwordStrengthColor = "#C03A31"; // weak
  } else if (passwordStrength < 67) {
    passwordStrengthColor = "#FFB032"; // medium
  } else {
    passwordStrengthColor = "#279627"; // strong
  }

  return (
    <div className="onboarding-container">
      <div className="onboard-content">
        <div className="d-flex flex-column justify-content-center align-items-center">
          <div className="progress-container">
            <div
              style={{
                background: "url('/icons/progress-tick.svg')",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
              className="step active"
              data-step="1"
            ></div>
            <div className="progress-line active" data-line="1"></div>
            <div
              style={{
                background: "url('/icons/progress-tick.svg')",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
              className="step active"
              data-step="2"
            ></div>
            <div className="progress-line active" data-line="2"></div>
            <div className="step active stepactive" data-step="3"></div>
            <div className="progress-line" data-line="3"></div>
            <div className="step" data-step="4"></div>
          </div>
          <h1
            style={{
              marginBottom: "40px",
              textAlign: "center",
              color: "rgba(230, 236, 241, 1)",
            }}
            className="text-heading-h3"
          >
            Add your details
            <span style={{ marginLeft: "12px" }}>3 / 4</span>
          </h1>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="d-flex flex-column align-items-center w-100 padding-card"
          >
            <div className="gap-3 gap-sm-4 d-flex flex-column align-items-center w-100">
              <div className="textfield-1 input-fields w-100">
                <TextInput
                  name="mobile"
                  control={control}
                  label="Enter mobile number"
                  placeholder=" "
                  rules={validationRules.mobile}
                  className={`text-body-b2 ${errors.mobile ? "error" : ""}`}
                />
                {errors.mobile && (
                  <div className="signup-error">{errors.mobile.message}</div>
                )}
              </div>

              <div className="textfield-1 input-fields w-100">
                <EmailInput
                  name="email"
                  control={control}
                  label="Enter email address"
                  placeholder=" "
                  rules={validationRules.email}
                  className="styled-input text-body-b2"
                />
                {errors.email && (
                  <div className="signup-error">{errors.email.message}</div>
                )}
              </div>

              <div className="textfield-1 input-fields password-container w-100">
                <PasswordInput
                  name="password"
                  control={control}
                  label="Enter password"
                  placeholder=" "
                  rules={validationRules.password}
                  className="text-body-b2 styled-input"
                />
                {errors.password && (
                  <div className="signup-error">{errors.password.message}</div>
                )}
              </div>

              <div className="textfield-1 input-fields password-container w-100">
                <PasswordInput
                  name="confirmPassword"
                  control={control}
                  label="Confirm password"
                  placeholder=" "
                  rules={validationRules.confirmPassword}
                  className="text-body-b2 styled-input"
                />
                {errors.confirmPassword && (
                  <div className="signup-error">
                    {errors.confirmPassword.message}
                  </div>
                )}
              </div>

              {/*{showPasswordMismatch && watchedConfirmPassword && (*/}
              {/*  <div*/}
              {/*    className="signup-error"*/}
              {/*    style={{*/}
              {/*      color: "#C03A31",*/}
              {/*      fontSize: "16px",*/}
              {/*      marginTop: "8px",*/}
              {/*      width: "100%",*/}
              {/*      display: "flex",*/}
              {/*      justifyContent: "center",*/}
              {/*      alignItems: "center",*/}
              {/*    }}*/}
              {/*  >*/}
              {/*    Passwords do not match*/}
              {/*  </div>*/}
              {/*)}*/}
              <div className="progress-bar-container">
                <div
                  id="passwordStrength"
                  className="progress-bar"
                  style={{
                    width: `${passwordStrength}%`,
                    backgroundColor: passwordStrengthColor,
                  }}
                ></div>
              </div>
              <div className="password-checklist">
                <span
                  id="lengthCheck"
                  className={
                    watchedPassword && watchedPassword.length >= 8
                      ? "valid"
                      : ""
                  }
                >
                  8 characters minimum
                </span>
                <span
                  id="numberCheck"
                  className={
                    watchedPassword && /\d/.test(watchedPassword) ? "valid" : ""
                  }
                >
                  a number
                </span>
                <span
                  id="symbolCheck"
                  className={
                    watchedPassword &&
                    /[!@#$%^&*(),.?":{}|<>]/.test(watchedPassword)
                      ? "valid"
                      : ""
                  }
                >
                  a symbol
                </span>
              </div>

              <div
                className="d-flex flex-column flex-sm-row gap-3 gap-sm-4 terms-buttons justify-content-between align-items-center w-100"
                style={{
                  maxWidth: "345px",
                  marginTop: "30px",
                  marginBottom: "40px",
                }}
              >
                <ContainedButton
                  onClick={() => navigate("/signup/profile")}
                  className="btn-1 btn-1-basic"
                  style={{ maxWidth: "160px", width: "100%" }}
                >
                  Back
                </ContainedButton>

                <ContainedButton
                  type="submit"
                  isFormValid={isValid}
                  className="btn-1"
                  isLoading={isCheckingUnique}
                  style={{
                    backgroundColor: isCheckingUnique
                      ? "rgba(153, 209, 255, 1)"
                      : isValid
                        ? "rgba(0, 140, 255, 1)"
                        : "rgba(153, 209, 255, 1)",
                    color: "rgba(230, 236, 241, 1)",
                    border: "1.5px solid rgba(0, 140, 255, 1)",
                    maxWidth: "160px",
                    width: "100%",
                    cursor:
                      isValid || isCheckingUnique ? "pointer" : "not-allowed",
                  }}
                >
                  {isCheckingUnique ? "Checking..." : "Next"}
                </ContainedButton>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
